/* eslint-disable @typescript-eslint/no-explicit-any */
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { format, parseISO, isValid } from "date-fns";
import { ptBR } from "date-fns/locale";

/**
 * FleetONE — Maintenance PDF PRO (A4 nativo, corporativo)
 * - NÃO imprime HTML.
 * - Desenha layout profissional usando jsPDF + autoTable.
 * - INCLUI detalhamento completo por veículo (planos, OSs, pendências)
 */

/* --------------------------
 * Utilities
 * -------------------------- */

/**
 * Sanitiza texto para PDF (jsPDF não suporta UTF-8 com fonte padrão Helvetica)
 * Remove acentos e caracteres especiais mantendo legibilidade
 */
function sanitizeForPdf(text: string | null | undefined): string {
  if (!text) return "-";
  return text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // Remove diacritics (acentos)
    .replace(/[^\x00-\x7F]/g, ""); // Remove any remaining non-ASCII
}

/* --------------------------
 * Types
 * -------------------------- */

export type PdfMode = "executive" | "top-n" | "complete";

export type ProviderRankingItem = {
  providerId: string;
  providerName: string;
  ordersCount: number;
  totalCost: number;
  avgCost: number;
};

export type VehicleMaintenanceItem = {
  vehicleId: string;
  vehicleLabel: string;
  ordersCount: number;
  totalCost: number;
  lastMaintenance: string | null;
};

export type PendingMaintenanceItem = {
  vehicleLabel: string;
  planName: string;
  nextDate: string | null;
  daysUntil: number | null;
  kmUntil: number | null;
  status: 'overdue' | 'critical' | 'upcoming' | 'scheduled';
};

export type WorkOrderItem = {
  orderNumber: string;
  vehicleLabel: string;
  providerName: string;
  type: string;
  cost: number;
  openedAt: string;
  closedAt: string | null;
  status: string;
};

export type MaintenancePdfKpis = {
  totalWorkOrders: number;
  completedOrders: number;
  openOrders: number;
  totalCost: number;
  avgCostPerOrder: number;
  pendingCount: number;
  overdueCount: number;
  criticalCount: number;
  pendingApprovalCount: number; // NOVO: Planos pendentes de aprovação
};

// NOVO: Plano pendente de aprovação
export type PendingApprovalPlan = {
  id: string;
  vehicleLabel: string;
  planName: string;
  type: string;
  periodicityLabel: string;
  createdAt: string;
  createdByName?: string;
};

// NOVO: Detalhes de manutenção por veículo
export type VehicleMaintenancePlan = {
  planId: string;
  planName: string;
  type: string;
  interval: string;
  lastExecution: string | null;
  nextExecution: string | null;
  status: 'ok' | 'upcoming' | 'critical' | 'overdue' | 'inactive' | 'pending' | 'awaiting_approval';
};

export type VehicleWorkOrder = {
  orderNumber: string;
  type: string;
  providerName: string;
  openedAt: string;
  closedAt: string | null;
  cost: number;
  status: string;
  planId?: string;
};

// NOVO: Plano com suas OSs agrupadas
export type VehicleMaintenancePlanWithOrders = {
  planId: string;
  planName: string;
  type: string;
  interval: string;
  lastExecution: string | null;
  nextExecution: string | null;
  status: 'ok' | 'upcoming' | 'critical' | 'overdue' | 'inactive' | 'pending' | 'awaiting_approval';
  workOrders: VehicleWorkOrder[];
};

export type VehicleMaintenanceDetail = {
  vehicle_id: string;
  vehicle_label: string;
  model?: string;
  
  totalOrders: number;
  completedOrders: number;
  openOrders: number;
  totalCost: number;
  avgCostPerOrder: number;
  lastMaintenanceDate: string | null;
  
  // Estrutura hierárquica: Planos com suas OSs agrupadas
  plansWithOrders: VehicleMaintenancePlanWithOrders[];
  
  // OSs sem plano vinculado (órfãs)
  orphanOrders: VehicleWorkOrder[];
  
  // Legado - manter para compatibilidade
  plans: VehicleMaintenancePlan[];
  workOrders: VehicleWorkOrder[];
  pendingPlans: PendingMaintenanceItem[];
};

export type PdfVehicleMaintenanceSummary = {
  vehicle_id: string;
  vehicle_label: string;
  ordersCount: number;
  totalCost: number;
};

export type MaintenancePdfInput = {
  companyName: string;
  periodLabel: string;
  generatedAtLabel: string;
  
  kpis: MaintenancePdfKpis;
  providerRanking: ProviderRankingItem[];
  vehicleRanking: VehicleMaintenanceItem[];
  pendingMaintenance: PendingMaintenanceItem[];
  recentWorkOrders: WorkOrderItem[];
  pendingApprovalPlans: PendingApprovalPlan[]; // NOVO: Planos pendentes de aprovação
  
  insights: string[];
  conclusionText: string;

  // NOVOS: Para detalhamento completo por veículo
  vehiclesSummary: PdfVehicleMaintenanceSummary[];
  fetchVehicleMaintenanceDetail: (vehicleId: string) => Promise<VehicleMaintenanceDetail>;
  options: { mode: PdfMode; topN?: number; maxVehicles?: number };
};

/* --------------------------
 * Theme / Helpers
 * -------------------------- */

const A4 = { w: 210, h: 297 };
const M = { l: 14, r: 14, t: 14, b: 14 };
const COLORS = {
  ink: [15, 23, 42] as [number, number, number],
  muted: [100, 116, 139] as [number, number, number],
  border: [226, 232, 240] as [number, number, number],
  panel: [248, 250, 252] as [number, number, number],
  header: [2, 6, 23] as [number, number, number],
  headRow: [241, 245, 249] as [number, number, number],
  zebra: [250, 250, 251] as [number, number, number],
  ok: [16, 185, 129] as [number, number, number],
  warn: [245, 158, 11] as [number, number, number],
  crit: [239, 68, 68] as [number, number, number],
};

function moneyBRL(n: number) {
  return (Number(n) || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function numberBR(n: number) {
  return (Number(n) || 0).toLocaleString("pt-BR");
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return "-";
  try {
    const date = parseISO(dateStr);
    if (!isValid(date)) return "-";
    return format(date, "dd/MM/yyyy", { locale: ptBR });
  } catch {
    return "-";
  }
}

function setFont(doc: jsPDF, weight: "normal" | "bold" = "normal") {
  doc.setFont("helvetica", weight);
}

function drawHeader(doc: jsPDF, title: string, subtitle: string) {
  doc.setFillColor(...COLORS.header);
  doc.roundedRect(M.l, M.t, A4.w - M.l - M.r, 22, 4, 4, "F");

  doc.setTextColor(255, 255, 255);
  setFont(doc, "bold");
  doc.setFontSize(12);
  doc.text("FleetONE", M.l + 6, M.t + 8);

  doc.setFontSize(14);
  doc.text(sanitizeForPdf(title), M.l + 6, M.t + 16);

  setFont(doc, "normal");
  doc.setFontSize(9);
  doc.setTextColor(226, 232, 240);
  doc.text(sanitizeForPdf(subtitle), M.l + 6, M.t + 20);

  doc.setTextColor(...COLORS.ink);
}

function drawFooter(doc: jsPDF, meta: { generatedAtLabel: string }) {
  const page = doc.getNumberOfPages();
  const y = A4.h - 10;

  doc.setDrawColor(...COLORS.border);
  doc.line(M.l, y - 4, A4.w - M.r, y - 4);

  doc.setTextColor(...COLORS.muted);
  doc.setFontSize(8);
  setFont(doc, "normal");
  doc.text(`Gerado em ${meta.generatedAtLabel}`, M.l, y);
  doc.text(`Pagina ${page}`, A4.w - M.r, y, { align: "right" });

  doc.setTextColor(...COLORS.ink);
}

function panel(doc: jsPDF, x: number, y: number, w: number, h: number) {
  doc.setDrawColor(...COLORS.border);
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(x, y, w, h, 4, 4, "FD");
}

function kpiCard(doc: jsPDF, x: number, y: number, w: number, h: number, opts: {
  label: string;
  value: string;
  sub?: string;
  badge?: { text: string; tone: "ok" | "warn" | "crit" };
}) {
  panel(doc, x, y, w, h);

  doc.setTextColor(...COLORS.muted);
  doc.setFontSize(8);
  setFont(doc, "normal");
  doc.text(sanitizeForPdf(opts.label), x + 4, y + 6);

  doc.setTextColor(...COLORS.ink);
  doc.setFontSize(12);
  setFont(doc, "bold");
  doc.text(sanitizeForPdf(opts.value), x + 4, y + 14);

  if (opts.sub) {
    doc.setTextColor(...COLORS.muted);
    doc.setFontSize(8);
    setFont(doc, "normal");
    doc.text(sanitizeForPdf(opts.sub), x + 4, y + 19);
  }

  if (opts.badge) {
    const col =
      opts.badge.tone === "crit" ? COLORS.crit : opts.badge.tone === "warn" ? COLORS.warn : COLORS.ok;

    doc.setFillColor(...col);
    doc.roundedRect(x + w - 24, y + 4, 20, 6.5, 3, 3, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(7);
    setFont(doc, "bold");
    doc.text(sanitizeForPdf(opts.badge.text), x + w - 14, y + 8.5, { align: "center" });

    doc.setTextColor(...COLORS.ink);
  }
}

function sectionTitle(doc: jsPDF, x: number, y: number, title: string, subtitle?: string) {
  setFont(doc, "bold");
  doc.setFontSize(11);
  doc.setTextColor(...COLORS.ink);
  doc.text(sanitizeForPdf(title), x, y);

  if (subtitle) {
    setFont(doc, "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(...COLORS.muted);
    doc.text(sanitizeForPdf(subtitle), x, y + 4);
  }
  doc.setTextColor(...COLORS.ink);
}

function tablePro(doc: jsPDF, opts: {
  startY: number;
  head: string[];
  body: any[][];
  marginLeft?: number;
  marginRight?: number;
}) {
  autoTable(doc, {
    startY: opts.startY,
    head: [opts.head],
    body: opts.body,
    theme: "grid",
    styles: {
      fontSize: 8,
      cellPadding: 3,
      textColor: COLORS.ink,
      lineColor: COLORS.border,
    },
    headStyles: {
      fillColor: COLORS.headRow,
      textColor: COLORS.muted,
      fontStyle: "bold",
    },
    alternateRowStyles: { fillColor: COLORS.zebra },
    margin: {
      left: opts.marginLeft ?? M.l,
      right: opts.marginRight ?? M.r,
    },
  });
  return (doc as any).lastAutoTable?.finalY ?? opts.startY;
}

function emptyBlockExec(doc: jsPDF, y: number, msg: string): number {
  doc.setFillColor(...COLORS.panel);
  doc.roundedRect(M.l, y, A4.w - M.l - M.r, 12, 2, 2, "F");
  doc.setTextColor(...COLORS.muted);
  doc.setFontSize(9);
  setFont(doc, "normal");
  doc.text(sanitizeForPdf(msg), M.l + 4, y + 8);
  doc.setTextColor(...COLORS.ink);
  return y + 16;
}

/* --------------------------
 * Drawing Functions
 * -------------------------- */

function drawProviderRanking(doc: jsPDF, ranking: ProviderRankingItem[], startY: number): number {
  sectionTitle(doc, M.l, startY, "Ranking de Fornecedores", "Por valor total de servicos");
  const y = startY + 8;

  const rows = (ranking ?? []).slice(0, 10).map((r) => ([
    sanitizeForPdf(r.providerName),
    String(r.ordersCount),
    moneyBRL(r.totalCost),
    moneyBRL(r.avgCost),
  ]));

  if (rows.length === 0) {
    return emptyBlockExec(doc, y, "Sem dados de fornecedores no periodo.");
  }

  const finalY = tablePro(doc, {
    startY: y,
    head: ["Fornecedor", "Qtd OS", "Total", "Media/OS"],
    body: rows,
  });

  return finalY + 8;
}

function drawVehicleRanking(doc: jsPDF, ranking: VehicleMaintenanceItem[], startY: number): number {
  sectionTitle(doc, M.l, startY, "Veiculos com Maior Custo de Manutencao", "Top 10 por investimento");
  const y = startY + 8;

  const rows = (ranking ?? []).slice(0, 10).map((r) => ([
    sanitizeForPdf(r.vehicleLabel),
    String(r.ordersCount),
    moneyBRL(r.totalCost),
    formatDate(r.lastMaintenance),
  ]));

  if (rows.length === 0) {
    return emptyBlockExec(doc, y, "Sem dados de veiculos no periodo.");
  }

  const finalY = tablePro(doc, {
    startY: y,
    head: ["Veiculo", "Qtd OS", "Custo Total", "Ultima Manutencao"],
    body: rows,
  });

  return finalY + 8;
}

function drawPendingMaintenance(doc: jsPDF, pending: PendingMaintenanceItem[], startY: number): number {
  sectionTitle(doc, M.l, startY, "Manutencoes Pendentes", "Planos proximos ou vencidos");
  const y = startY + 8;

  const statusLabel = (s: string) => {
    const map: Record<string, string> = {
      overdue: "VENCIDO",
      critical: "CRITICO",
      upcoming: "PROXIMO",
      scheduled: "AGENDADO",
    };
    return map[s] || s;
  };

  const rows = (pending ?? []).slice(0, 15).map((r) => ([
    sanitizeForPdf(r.vehicleLabel),
    sanitizeForPdf(r.planName),
    r.nextDate ? formatDate(r.nextDate) : "-",
    r.daysUntil !== null ? `${r.daysUntil} dias` : "-",
    r.kmUntil !== null ? `${numberBR(r.kmUntil)} km` : "-",
    statusLabel(r.status),
  ]));

  if (rows.length === 0) {
    return emptyBlockExec(doc, y, "Nenhuma manutencao pendente.");
  }

  const finalY = tablePro(doc, {
    startY: y,
    head: ["Veiculo", "Plano", "Prox. Data", "Dias", "Km Restante", "Status"],
    body: rows,
  });

  return finalY + 8;
}

function drawRecentWorkOrders(doc: jsPDF, orders: WorkOrderItem[], startY: number): number {
  sectionTitle(doc, M.l, startY, "Ordens de Servico Recentes", "Ultimas 10 OS");
  const y = startY + 8;

  const statusLabel = (s: string) => {
    const map: Record<string, string> = {
      open: "Aberta",
      closed: "Concluida",
      pending: "Pendente",
      in_progress: "Em Andamento",
      cancelled: "Cancelada",
    };
    return map[s?.toLowerCase()] || s;
  };

  const rows = (orders ?? []).slice(0, 10).map((r) => ([
    r.orderNumber,
    sanitizeForPdf(r.vehicleLabel),
    sanitizeForPdf(r.providerName),
    moneyBRL(r.cost),
    formatDate(r.openedAt),
    statusLabel(r.status),
  ]));

  if (rows.length === 0) {
    return emptyBlockExec(doc, y, "Nenhuma OS no periodo.");
  }

  const finalY = tablePro(doc, {
    startY: y,
    head: ["OS", "Veiculo", "Fornecedor", "Custo", "Abertura", "Status"],
    body: rows,
  });

  return finalY + 8;
}

// NOVO: Planos pendentes de aprovação
function drawPendingApprovalPlans(doc: jsPDF, plans: PendingApprovalPlan[], startY: number): number {
  sectionTitle(doc, M.l, startY, "Planos Pendentes de Aprovacao", "Aguardando liberacao do gestor");
  const y = startY + 8;

  const rows = (plans ?? []).slice(0, 15).map((p) => ([
    sanitizeForPdf(p.vehicleLabel),
    sanitizeForPdf(p.planName),
    sanitizeForPdf(p.type),
    sanitizeForPdf(p.periodicityLabel),
    formatDate(p.createdAt),
    "PENDENTE",
  ]));

  if (rows.length === 0) {
    return emptyBlockExec(doc, y, "Nenhum plano pendente de aprovacao.");
  }

  const finalY = tablePro(doc, {
    startY: y,
    head: ["Veiculo", "Plano", "Tipo", "Periodicidade", "Criado em", "Status"],
    body: rows,
  });

  return finalY + 8;
}

function drawInsightsBlock(doc: jsPDF, insights: string[], startY: number): number {
  const list = (insights ?? []).slice(0, 6);
  const insightsH = 10 + Math.max(list.length, 3) * 5.5;
  
  panel(doc, M.l, startY, A4.w - M.l - M.r, insightsH);
  sectionTitle(doc, M.l + 4, startY + 8, "Principais Insights");

  setFont(doc, "normal");
  doc.setFontSize(9);

  let ly = startY + 16;
  list.forEach((t) => {
    doc.setTextColor(...COLORS.muted);
    doc.text("*", M.l + 6, ly);
    doc.setTextColor(...COLORS.ink);
    const wrapped = doc.splitTextToSize(sanitizeForPdf(String(t)), A4.w - M.l - M.r - 16);
    doc.text(wrapped, M.l + 10, ly);
    ly += wrapped.length * 4.5;
  });

  return startY + insightsH + 6;
}

function drawConclusionBlock(doc: jsPDF, data: MaintenancePdfInput, startY: number): number {
  const summaryText = `Foram registradas ${data.kpis.totalWorkOrders} ordens de servico no periodo, com custo total de ${moneyBRL(data.kpis.totalCost)}. Taxa de conclusao: ${((data.kpis.completedOrders / (data.kpis.totalWorkOrders || 1)) * 100).toFixed(0)}%. ${data.kpis.overdueCount > 0 ? `Atencao: ${data.kpis.overdueCount} manutencoes vencidas.` : 'Nenhuma manutencao vencida.'}`;

  const wrappedSummary = doc.splitTextToSize(sanitizeForPdf(summaryText), A4.w - M.l - M.r - 12);
  const conclusionH = 24 + wrappedSummary.length * 4;

  panel(doc, M.l, startY, A4.w - M.l - M.r, conclusionH);
  sectionTitle(doc, M.l + 4, startY + 8, "Conclusao Executiva");

  doc.setFontSize(9);
  setFont(doc, "normal");
  doc.setTextColor(...COLORS.ink);
  doc.text(wrappedSummary, M.l + 4, startY + 16);

  return startY + conclusionH + 6;
}

/* --------------------------
 * Main Pages
 * -------------------------- */

function addExecutivePage(doc: jsPDF, data: MaintenancePdfInput) {
  const title = "Relatorio de Manutencao — Visao Executiva";
  const subtitle = `${data.periodLabel} • ${data.companyName}`;

  drawHeader(doc, title, subtitle);

  // KPIs (2 linhas x 4 col)
  const topY = 40;
  const gap = 4;
  const w = (A4.w - M.l - M.r - gap * 3) / 4;
  const h = 22;

  const completionRate = data.kpis.totalWorkOrders > 0 
    ? ((data.kpis.completedOrders / data.kpis.totalWorkOrders) * 100).toFixed(0) 
    : "0";

  kpiCard(doc, M.l, topY, w, h, {
    label: "Total de OS",
    value: String(data.kpis.totalWorkOrders),
  });

  kpiCard(doc, M.l + w + gap, topY, w, h, {
    label: "Concluidas",
    value: String(data.kpis.completedOrders),
    sub: `${completionRate}% do total`,
    badge: { text: "OK", tone: "ok" },
  });

  kpiCard(doc, M.l + (w + gap) * 2, topY, w, h, {
    label: "Em Aberto",
    value: String(data.kpis.openOrders),
  });

  kpiCard(doc, M.l + (w + gap) * 3, topY, w, h, {
    label: "Custo Total",
    value: moneyBRL(data.kpis.totalCost),
  });

  // Segunda linha
  const row2Y = topY + h + gap;

  kpiCard(doc, M.l, row2Y, w, h, {
    label: "Custo Medio/OS",
    value: moneyBRL(data.kpis.avgCostPerOrder),
  });

  kpiCard(doc, M.l + w + gap, row2Y, w, h, {
    label: "Pendentes",
    value: String(data.kpis.pendingCount),
    badge: data.kpis.pendingCount > 10 ? { text: "Atencao", tone: "warn" } : undefined,
  });

  kpiCard(doc, M.l + (w + gap) * 2, row2Y, w, h, {
    label: "Vencidas",
    value: String(data.kpis.overdueCount),
    badge: data.kpis.overdueCount > 0 ? { text: "Critico", tone: "crit" } : { text: "OK", tone: "ok" },
  });

  kpiCard(doc, M.l + (w + gap) * 3, row2Y, w, h, {
    label: "Pend. Aprovacao",
    value: String(data.kpis.pendingApprovalCount),
    badge: data.kpis.pendingApprovalCount > 0 ? { text: "ACAO", tone: "warn" } : undefined,
  });

  let yPos = row2Y + h + 12;

  // Ranking de fornecedores
  yPos = drawProviderRanking(doc, data.providerRanking, yPos);

  // Ranking de veículos
  if (yPos + 60 > A4.h - 30) {
    doc.addPage();
    drawHeader(doc, "Relatorio de Manutencao — Por Veiculo", subtitle);
    yPos = 44;
  }
  yPos = drawVehicleRanking(doc, data.vehicleRanking, yPos);

  // OS Recentes
  if (yPos + 60 > A4.h - 30) {
    doc.addPage();
    drawHeader(doc, "Relatorio de Manutencao — Ordens de Servico", subtitle);
    yPos = 44;
  }
  yPos = drawRecentWorkOrders(doc, data.recentWorkOrders, yPos);

  // Pendentes
  if (data.pendingMaintenance.length > 0) {
    if (yPos + 60 > A4.h - 30) {
      doc.addPage();
      drawHeader(doc, "Relatorio de Manutencao — Pendencias", subtitle);
      yPos = 44;
    }
    yPos = drawPendingMaintenance(doc, data.pendingMaintenance, yPos);
  }

  // NOVO: Planos Pendentes de Aprovação
  if (data.pendingApprovalPlans.length > 0) {
    if (yPos + 60 > A4.h - 30) {
      doc.addPage();
      drawHeader(doc, "Relatorio de Manutencao — Pendentes de Aprovacao", subtitle);
      yPos = 44;
    }
    yPos = drawPendingApprovalPlans(doc, data.pendingApprovalPlans, yPos);
  }

  // Insights
  if (data.insights.length > 0) {
    if (yPos + 50 > A4.h - 30) {
      doc.addPage();
      drawHeader(doc, "Relatorio de Manutencao — Insights", subtitle);
      yPos = 44;
    }
    yPos = drawInsightsBlock(doc, data.insights, yPos);
  }

  // Conclusão
  if (yPos + 40 > A4.h - 30) {
    doc.addPage();
    drawHeader(doc, "Relatorio de Manutencao — Conclusao", subtitle);
    yPos = 44;
  }
  drawConclusionBlock(doc, data, yPos);
}

/* --------------------------
 * Vehicle Detail Page Helpers
 * -------------------------- */

function ensureSpace(
  doc: jsPDF,
  currentY: number,
  neededHeight: number,
  onNewPage: () => void
): number {
  const bottomLimit = A4.h - M.b - 12;
  if (currentY + neededHeight > bottomLimit) {
    doc.addPage();
    onNewPage();
    return M.t + 50;
  }
  return currentY;
}

function emptyBlockDetail(doc: jsPDF, y: number, msg: string): number {
  doc.setFillColor(...COLORS.panel);
  doc.roundedRect(M.l, y, A4.w - M.l - M.r, 12, 2, 2, "F");
  doc.setTextColor(...COLORS.muted);
  doc.setFontSize(9);
  setFont(doc, "normal");
  doc.text(sanitizeForPdf(msg), M.l + 4, y + 8);
  doc.setTextColor(...COLORS.ink);
  return y + 16;
}

function drawVehicleHeaderChip(doc: jsPDF, vehicleLabel: string) {
  const y0 = M.t + 26;
  panel(doc, M.l, y0, A4.w - M.l - M.r, 14);
  setFont(doc, "normal");
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.muted);
  doc.text("Veiculo:", M.l + 4, y0 + 9);
  setFont(doc, "bold");
  doc.setTextColor(...COLORS.ink);
  doc.text(sanitizeForPdf(vehicleLabel), M.l + 22, y0 + 9);
}

function drawVehicleMaintenanceKpis(
  doc: jsPDF,
  detail: VehicleMaintenanceDetail,
  y: number
): number {
  const gap = 4;
  const w = (A4.w - M.l - M.r - gap * 2) / 3;
  const h = 20;

  kpiCard(doc, M.l, y, w, h, {
    label: "Total de OS",
    value: String(detail.totalOrders),
    sub: `${detail.completedOrders} concluidas`,
  });
  kpiCard(doc, M.l + (w + gap), y, w, h, {
    label: "Custo total",
    value: moneyBRL(detail.totalCost),
  });
  kpiCard(doc, M.l + 2 * (w + gap), y, w, h, {
    label: "Ultima manutencao",
    value: formatDate(detail.lastMaintenanceDate),
  });

  return y + h + 6;
}

function tableProDetail(doc: jsPDF, opts: {
  startY: number;
  head: string[];
  body: any[][];
  marginLeft?: number;
  marginRight?: number;
  marginTopOnNewPage?: number;
  onEveryPageHeader?: () => void;
}) {
  const topMarginForNewPages = opts.marginTopOnNewPage ?? M.t + 44;
  
  autoTable(doc, {
    startY: opts.startY,
    head: [opts.head],
    body: opts.body,
    theme: "grid",
    styles: {
      fontSize: 8,
      cellPadding: 3,
      textColor: COLORS.ink,
      lineColor: COLORS.border,
    },
    headStyles: {
      fillColor: COLORS.headRow,
      textColor: COLORS.muted,
      fontStyle: "bold",
    },
    alternateRowStyles: { fillColor: COLORS.zebra },
    margin: {
      left: opts.marginLeft ?? M.l,
      right: opts.marginRight ?? M.r,
      top: topMarginForNewPages,
    },
    didDrawPage: () => {
      opts.onEveryPageHeader?.();
    },
  });
  return (doc as any).lastAutoTable?.finalY ?? opts.startY;
}

function drawCategoryDetail(
  doc: jsPDF,
  opts: {
    title: string;
    columns: string[];
    rows: any[][];
    emptyMsg: string;
  },
  y: number,
  onNewPage: () => void
): number {
  y = ensureSpace(doc, y, 18, onNewPage);
  sectionTitle(doc, M.l, y, opts.title);
  y += 6;

  if (!opts.rows || opts.rows.length === 0) {
    return emptyBlockDetail(doc, y, opts.emptyMsg);
  }

  y = ensureSpace(doc, y, 24, onNewPage);
  const finalY = tableProDetail(doc, {
    startY: y,
    head: opts.columns,
    body: opts.rows,
    marginTopOnNewPage: M.t + 50,
    onEveryPageHeader: onNewPage,
  });

  return finalY + 6;
}

function statusLabel(s: string): string {
  const map: Record<string, string> = {
    overdue: "VENCIDO",
    critical: "CRITICO",
    upcoming: "PROXIMO",
    scheduled: "AGENDADO",
    ok: "OK",
    open: "Aberta",
    closed: "Concluida",
    pending: "PENDENTE",
    awaiting_approval: "AG. APROV.",
    in_progress: "Em Andamento",
    cancelled: "Cancelada",
    inactive: "INATIVO",
  };
  return map[s?.toLowerCase()] || s;
}

function drawPlanWithOrdersSection(
  doc: jsPDF,
  plan: VehicleMaintenancePlanWithOrders,
  y: number,
  onNewPage: () => void
): number {
  // Cabeçalho do plano
  y = ensureSpace(doc, y, 24, onNewPage);
  
  // Fundo do cabeçalho do plano
  doc.setFillColor(...COLORS.panel);
  doc.roundedRect(M.l, y, A4.w - M.l - M.r, 14, 2, 2, "F");
  
  // Nome do plano (sem emoji - usar [+])
  setFont(doc, "bold");
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.ink);
  doc.text(`[+] ${sanitizeForPdf(plan.planName)}`, M.l + 4, y + 6);
  
  // Tipo e intervalo
  setFont(doc, "normal");
  doc.setFontSize(8);
  doc.setTextColor(...COLORS.muted);
  const typeLabel = plan.type === 'preventive' ? 'Preventiva' : plan.type === 'corrective' ? 'Corretiva' : sanitizeForPdf(plan.type) || '-';
  doc.text(`${typeLabel} - ${sanitizeForPdf(plan.interval)}`, M.l + 4, y + 11);
  
  // Status badge - incluindo novos status 'pending' e 'awaiting_approval'
  const statusColors: Record<string, [number, number, number]> = {
    ok: COLORS.ok,
    upcoming: COLORS.warn,
    critical: COLORS.crit,
    overdue: COLORS.crit,
    inactive: COLORS.muted,
    pending: [59, 130, 246] as [number, number, number], // Azul (blue-500)
    awaiting_approval: [245, 158, 11] as [number, number, number], // Amarelo (amber-500)
  };
  const badgeColor = statusColors[plan.status] || COLORS.muted;
  const statusText = statusLabel(plan.status);
  
  doc.setFillColor(...badgeColor);
  doc.roundedRect(A4.w - M.r - 28, y + 3, 24, 6, 3, 3, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(7);
  setFont(doc, "bold");
  doc.text(statusText, A4.w - M.r - 16, y + 7.5, { align: "center" });
  
  // Info de próxima/última execução
  doc.setTextColor(...COLORS.muted);
  doc.setFontSize(7);
  setFont(doc, "normal");
  const execInfo = [];
  if (plan.nextExecution) execInfo.push(`Prox: ${formatDate(plan.nextExecution)}`);
  if (plan.lastExecution) execInfo.push(`Ult: ${formatDate(plan.lastExecution)}`);
  if (execInfo.length > 0) {
    doc.text(execInfo.join(" | "), A4.w - M.r - 60, y + 11);
  }
  
  doc.setTextColor(...COLORS.ink);
  y += 18;
  
  // Tabela de OSs do plano (indentada)
  if (plan.workOrders.length > 0) {
    const osRows = plan.workOrders.map((o) => [
      o.orderNumber,
      sanitizeForPdf(o.type),
      sanitizeForPdf(o.providerName),
      formatDate(o.openedAt),
      formatDate(o.closedAt),
      moneyBRL(o.cost),
      statusLabel(o.status),
    ]);
    
    y = ensureSpace(doc, y, 20, onNewPage);
    const finalY = tableProDetail(doc, {
      startY: y,
      head: ["OS", "Tipo", "Fornecedor", "Abertura", "Conclusao", "Custo", "Status"],
      body: osRows,
      marginLeft: M.l + 6, // Indentação visual
      marginTopOnNewPage: M.t + 50,
      onEveryPageHeader: onNewPage,
    });
    y = finalY + 4;
  } else {
    doc.setFillColor(...COLORS.panel);
    doc.roundedRect(M.l + 6, y, A4.w - M.l - M.r - 6, 10, 2, 2, "F");
    doc.setTextColor(...COLORS.muted);
    doc.setFontSize(8);
    setFont(doc, "normal");
    doc.text("Nenhuma OS executada para este plano", M.l + 10, y + 6);
    doc.setTextColor(...COLORS.ink);
    y += 14;
  }
  
  return y + 2;
}

function addVehicleMaintenancePage(
  doc: jsPDF,
  input: MaintenancePdfInput,
  summary: PdfVehicleMaintenanceSummary,
  detail: VehicleMaintenanceDetail
) {
  const title = "Manutencao por Veiculo — Planos e OS";
  const subtitle = `${sanitizeForPdf(input.periodLabel)} - ${sanitizeForPdf(input.companyName)}`;

  doc.addPage();
  drawHeader(doc, title, subtitle);
  drawVehicleHeaderChip(doc, summary.vehicle_label);

  const onNewPage = () => {
    drawHeader(doc, title, subtitle);
    drawVehicleHeaderChip(doc, summary.vehicle_label);
  };

  let y = M.t + 48;

  // KPIs do veículo
  y = drawVehicleMaintenanceKpis(doc, detail, y);

  // NOVA ESTRUTURA: Planos com OSs agrupadas hierarquicamente
  if (detail.plansWithOrders && detail.plansWithOrders.length > 0) {
    y = ensureSpace(doc, y, 12, onNewPage);
    sectionTitle(doc, M.l, y, "Planos de Manutencao e Ordens de Servico");
    y += 8;
    
    for (const plan of detail.plansWithOrders) {
      y = drawPlanWithOrdersSection(doc, plan, y, onNewPage);
    }
  } else {
    // Fallback para estrutura antiga se plansWithOrders não existir
    const planRows = (detail.plans ?? []).map((p) => [
      sanitizeForPdf(p.planName),
      sanitizeForPdf(p.interval),
      formatDate(p.lastExecution),
      formatDate(p.nextExecution),
      statusLabel(p.status),
    ]);
    y = drawCategoryDetail(
      doc,
      {
        title: "Planos de Manutencao",
        columns: ["Plano", "Intervalo", "Ultima Exec.", "Proxima Exec.", "Status"],
        rows: planRows,
        emptyMsg: "Nenhum plano atribuido a este veiculo.",
      },
      y,
      onNewPage
    );

    // Ordens de Serviço (estrutura antiga)
    const osRows = (detail.workOrders ?? []).map((o) => [
      o.orderNumber,
      sanitizeForPdf(o.type),
      sanitizeForPdf(o.providerName),
      formatDate(o.openedAt),
      formatDate(o.closedAt),
      moneyBRL(o.cost),
      statusLabel(o.status),
    ]);
    y = drawCategoryDetail(
      doc,
      {
        title: "Ordens de Servico",
        columns: ["OS", "Tipo", "Fornecedor", "Abertura", "Conclusao", "Custo", "Status"],
        rows: osRows,
        emptyMsg: "Nenhuma OS no periodo.",
      },
      y,
      onNewPage
    );
  }

  // OSs órfãs (sem plano vinculado)
  if (detail.orphanOrders && detail.orphanOrders.length > 0) {
    const orphanRows = detail.orphanOrders.map((o) => [
      o.orderNumber,
      sanitizeForPdf(o.type),
      sanitizeForPdf(o.providerName),
      formatDate(o.openedAt),
      formatDate(o.closedAt),
      moneyBRL(o.cost),
      statusLabel(o.status),
    ]);
    y = drawCategoryDetail(
      doc,
      {
        title: "Ordens de Servico sem Plano Vinculado",
        columns: ["OS", "Tipo", "Fornecedor", "Abertura", "Conclusao", "Custo", "Status"],
        rows: orphanRows,
        emptyMsg: "",
      },
      y,
      onNewPage
    );
  }

  // Pendências do veículo
  const pendingRows = (detail.pendingPlans ?? []).map((p) => [
    sanitizeForPdf(p.planName),
    p.nextDate ? formatDate(p.nextDate) : "-",
    p.daysUntil !== null ? `${p.daysUntil} dias` : "-",
    p.kmUntil !== null ? `${numberBR(p.kmUntil)} km` : "-",
    statusLabel(p.status),
  ]);
  y = drawCategoryDetail(
    doc,
    {
      title: "Proximas Manutencoes / Pendencias",
      columns: ["Plano", "Prox. Data", "Dias", "Km Restante", "Status"],
      rows: pendingRows,
      emptyMsg: "Nenhuma manutencao pendente.",
    },
    y,
    onNewPage
  );
}

/* --------------------------
 * Exports
 * -------------------------- */

export async function generateMaintenancePDF_Pro(input: MaintenancePdfInput): Promise<Blob> {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  
  addExecutivePage(doc, input);

  // NOVO: Adicionar páginas de detalhamento por veículo
  const mode = input.options?.mode ?? "top-n";
  if (mode !== "executive" && input.vehiclesSummary.length > 0 && input.fetchVehicleMaintenanceDetail) {
    const topN = input.options?.topN ?? 10;
    const maxVehicles = input.options?.maxVehicles ?? 80;

    let vehiclesToDetail = mode === "top-n"
      ? input.vehiclesSummary.slice(0, topN)
      : input.vehiclesSummary;

    const truncated = vehiclesToDetail.length > maxVehicles;
    if (truncated) {
      vehiclesToDetail = vehiclesToDetail.slice(0, maxVehicles);
    }

    for (const v of vehiclesToDetail) {
      try {
        const detail = await input.fetchVehicleMaintenanceDetail(v.vehicle_id);
        addVehicleMaintenancePage(doc, input, v, detail);
      } catch (err) {
        console.warn(`Erro ao buscar detalhes de manutenção do veículo ${v.vehicle_id}:`, err);
      }
    }

    // Aviso de truncamento
    if (truncated) {
      doc.addPage();
      drawHeader(doc, "Aviso de Truncamento", `${sanitizeForPdf(input.periodLabel)} - ${sanitizeForPdf(input.companyName)}`);
      doc.setFontSize(10);
      setFont(doc, "normal");
      doc.setTextColor(...COLORS.muted);
      doc.text(
        `Relatorio limitado a ${maxVehicles} veiculos. Total de veiculos: ${input.vehiclesSummary.length}.`,
        M.l,
        M.t + 40
      );
      doc.text("Para ver todos os veiculos, gere relatorios separados ou ajuste o filtro.", M.l, M.t + 48);
    }
  }

  // Footer em todas as páginas
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    drawFooter(doc, { generatedAtLabel: input.generatedAtLabel });
  }

  return doc.output("blob");
}

export function buildMaintenancePdfInput(params: {
  workOrders: any[];
  pendingPlans: any[];
  pendingApprovalPlans?: PendingApprovalPlan[]; // NOVO: Planos pendentes de aprovação
  periodLabel: string;
  companyName: string;
  // NOVOS: Para detalhamento por veículo
  vehiclesSummary?: PdfVehicleMaintenanceSummary[];
  fetchVehicleMaintenanceDetail?: (vehicleId: string) => Promise<VehicleMaintenanceDetail>;
  options?: { mode: PdfMode; topN?: number; maxVehicles?: number };
}): MaintenancePdfInput {
  const { workOrders, pendingPlans, periodLabel, companyName } = params;
  
  const generatedAtLabel = format(new Date(), "dd/MM/yyyy 'as' HH:mm", { locale: ptBR });
  
  // Calcular KPIs
  const totalWorkOrders = workOrders.length;
  const completedOrders = workOrders.filter(o => o.status === 'closed').length;
  const openOrders = workOrders.filter(o => o.status === 'open' || o.status === 'in_progress').length;
  const totalCost = workOrders.reduce((sum, o) => sum + (o.cost || o.total_cost || 0), 0);
  const avgCostPerOrder = totalWorkOrders > 0 ? totalCost / totalWorkOrders : 0;
  
  const overdueCount = pendingPlans.filter(p => 
    (p.days_until_maintenance !== null && p.days_until_maintenance < 0) ||
    (p.km_until_maintenance !== null && p.km_until_maintenance < 0)
  ).length;
  
  const criticalCount = pendingPlans.filter(p => 
    (p.days_until_maintenance !== null && p.days_until_maintenance >= 0 && p.days_until_maintenance <= 7) ||
    (p.km_until_maintenance !== null && p.km_until_maintenance >= 0 && p.km_until_maintenance < 500)
  ).length;

  // Ranking de fornecedores
  const providerMap = new Map<string, { name: string; count: number; cost: number }>();
  workOrders.forEach(o => {
    const name = o.provider_name || "Sem Fornecedor";
    const id = o.provider_id || name;
    const existing = providerMap.get(id) || { name, count: 0, cost: 0 };
    existing.count++;
    existing.cost += o.cost || o.total_cost || 0;
    providerMap.set(id, existing);
  });
  
  const providerRanking: ProviderRankingItem[] = Array.from(providerMap.entries())
    .map(([id, data]) => ({
      providerId: id,
      providerName: data.name,
      ordersCount: data.count,
      totalCost: data.cost,
      avgCost: data.count > 0 ? data.cost / data.count : 0,
    }))
    .sort((a, b) => b.totalCost - a.totalCost);

  // Ranking de veículos
  const vehicleMap = new Map<string, { label: string; count: number; cost: number; last: string | null }>();
  workOrders.forEach(o => {
    const id = o.vehicle_id;
    const label = o.vehicle_plate || "-";
    const existing = vehicleMap.get(id) || { label, count: 0, cost: 0, last: null };
    existing.count++;
    existing.cost += o.cost || o.total_cost || 0;
    if (!existing.last || (o.closed_at && o.closed_at > existing.last)) {
      existing.last = o.closed_at || o.opened_at;
    }
    vehicleMap.set(id, existing);
  });
  
  const vehicleRanking: VehicleMaintenanceItem[] = Array.from(vehicleMap.entries())
    .map(([id, data]) => ({
      vehicleId: id,
      vehicleLabel: data.label,
      ordersCount: data.count,
      totalCost: data.cost,
      lastMaintenance: data.last,
    }))
    .sort((a, b) => b.totalCost - a.totalCost);

  // Gerar vehiclesSummary a partir do vehicleRanking se não foi passado
  const vehiclesSummary: PdfVehicleMaintenanceSummary[] = params.vehiclesSummary ?? vehicleRanking.map(v => ({
    vehicle_id: v.vehicleId,
    vehicle_label: v.vehicleLabel,
    ordersCount: v.ordersCount,
    totalCost: v.totalCost,
  }));

  // Pendentes
  const pendingMaintenance: PendingMaintenanceItem[] = pendingPlans
    .map(p => {
      let status: 'overdue' | 'critical' | 'upcoming' | 'scheduled' = 'scheduled';
      
      const daysUntil = p.days_until_maintenance;
      const kmUntil = p.km_until_maintenance;
      
      if ((daysUntil !== null && daysUntil < 0) || (kmUntil !== null && kmUntil < 0)) {
        status = 'overdue';
      } else if ((daysUntil !== null && daysUntil <= 7) || (kmUntil !== null && kmUntil < 500)) {
        status = 'critical';
      } else if ((daysUntil !== null && daysUntil <= 30) || (kmUntil !== null && kmUntil < 2000)) {
        status = 'upcoming';
      }

      return {
        vehicleLabel: p.vehicle_plate || "-",
        planName: p.plan_name || p.name || "-",
        nextDate: p.next_execution_date,
        daysUntil,
        kmUntil,
        status,
      };
    })
    .sort((a, b) => {
      const order = { overdue: 0, critical: 1, upcoming: 2, scheduled: 3 };
      return order[a.status] - order[b.status];
    });

  // OS recentes
  const recentWorkOrders: WorkOrderItem[] = workOrders
    .sort((a, b) => (b.opened_at || "").localeCompare(a.opened_at || ""))
    .slice(0, 10)
    .map(o => ({
      orderNumber: o.order_number ? `OS-${String(o.order_number).padStart(5, '0')}` : "-",
      vehicleLabel: o.vehicle_plate || "-",
      providerName: o.provider_name || "Sem Fornecedor",
      type: o.maintenance_type || o.description || "Manutencao",
      cost: o.cost || o.total_cost || 0,
      openedAt: o.opened_at,
      closedAt: o.closed_at,
      status: o.status,
    }));

  // Insights dinâmicos
  const insights: string[] = [];
  
  if (providerRanking.length > 0) {
    const topProvider = providerRanking[0];
    insights.push(`Fornecedor mais utilizado: ${topProvider.providerName} (${topProvider.ordersCount} OS, ${moneyBRL(topProvider.totalCost)})`);
  }
  
  if (vehicleRanking.length > 0) {
    const topVehicle = vehicleRanking[0];
    insights.push(`Veiculo com maior custo de manutencao: ${topVehicle.vehicleLabel} (${moneyBRL(topVehicle.totalCost)})`);
  }

  if (overdueCount > 0) {
    insights.push(`ATENCAO: ${overdueCount} manutencoes vencidas requerem acao imediata`);
  }

  if (criticalCount > 0) {
    insights.push(`${criticalCount} manutencoes proximas do vencimento (7 dias ou 500km)`);
  }

  const completionRate = totalWorkOrders > 0 ? (completedOrders / totalWorkOrders) * 100 : 0;
  if (completionRate < 80) {
    insights.push(`Taxa de conclusao de ${completionRate.toFixed(0)}% esta abaixo do ideal`);
  } else {
    insights.push(`Taxa de conclusao de ${completionRate.toFixed(0)}% dentro do esperado`);
  }

  // Conclusão
  const conclusionText = `${totalWorkOrders} ordens de servico registradas. Custo total: ${moneyBRL(totalCost)}.`;

  // Default fetchVehicleMaintenanceDetail - retorna detalhes vazios se não fornecido
  const defaultFetch = async (vehicleId: string): Promise<VehicleMaintenanceDetail> => {
    const vehicle = vehicleRanking.find(v => v.vehicleId === vehicleId);
    const vehicleOrders = workOrders.filter(o => o.vehicle_id === vehicleId);
    const vehiclePending = pendingPlans.filter(p => p.vehicle_id === vehicleId);
    
    const mappedOrders = vehicleOrders.map(o => ({
      orderNumber: o.order_number ? `OS-${String(o.order_number).padStart(5, '0')}` : "-",
      type: o.maintenance_type || o.description || "Manutencao",
      providerName: o.provider_name || "Sem Fornecedor",
      openedAt: o.opened_at,
      closedAt: o.closed_at,
      cost: o.cost || o.total_cost || 0,
      status: o.status,
      planId: o.plan_id,
    }));
    
    const mappedPendingPlans = vehiclePending.map(p => {
      let status: 'overdue' | 'critical' | 'upcoming' | 'scheduled' = 'scheduled';
      const daysUntil = p.days_until_maintenance;
      const kmUntil = p.km_until_maintenance;
      if ((daysUntil !== null && daysUntil < 0) || (kmUntil !== null && kmUntil < 0)) {
        status = 'overdue';
      } else if ((daysUntil !== null && daysUntil <= 7) || (kmUntil !== null && kmUntil < 500)) {
        status = 'critical';
      } else if ((daysUntil !== null && daysUntil <= 30) || (kmUntil !== null && kmUntil < 2000)) {
        status = 'upcoming';
      }
      return {
        vehicleLabel: p.vehicle_plate || "-",
        planName: p.plan_name || p.name || "-",
        nextDate: p.next_execution_date,
        daysUntil,
        kmUntil,
        status,
      };
    });
    
    return {
      vehicle_id: vehicleId,
      vehicle_label: vehicle?.vehicleLabel ?? "-",
      totalOrders: vehicleOrders.length,
      completedOrders: vehicleOrders.filter(o => o.status === 'closed').length,
      openOrders: vehicleOrders.filter(o => o.status === 'open' || o.status === 'in_progress').length,
      totalCost: vehicleOrders.reduce((sum, o) => sum + (o.cost || o.total_cost || 0), 0),
      avgCostPerOrder: vehicleOrders.length > 0 
        ? vehicleOrders.reduce((sum, o) => sum + (o.cost || o.total_cost || 0), 0) / vehicleOrders.length 
        : 0,
      lastMaintenanceDate: vehicle?.lastMaintenance ?? null,
      plansWithOrders: [], // Será preenchido pela função real
      orphanOrders: mappedOrders, // Sem plano, todas são "órfãs" no default
      plans: [],
      workOrders: mappedOrders,
      pendingPlans: mappedPendingPlans,
    };
  };

  return {
    companyName,
    periodLabel,
    generatedAtLabel,
    kpis: {
      totalWorkOrders,
      completedOrders,
      openOrders,
      totalCost,
      avgCostPerOrder,
      pendingCount: pendingPlans.length,
      overdueCount,
      criticalCount,
      pendingApprovalCount: params.pendingApprovalPlans?.length ?? 0,
    },
    providerRanking,
    vehicleRanking,
    pendingMaintenance,
    recentWorkOrders,
    pendingApprovalPlans: params.pendingApprovalPlans ?? [],
    insights,
    conclusionText,
    vehiclesSummary,
    fetchVehicleMaintenanceDetail: params.fetchVehicleMaintenanceDetail ?? defaultFetch,
    options: params.options ?? { mode: "top-n", topN: 10, maxVehicles: 80 },
  };
}
